import { useMemo } from "react";
import { Link } from "react-router-dom";
import { EditIconButton, DeleteIconButton } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { PROJECT_DETAIL_TABS } from "../constants/tabs";

export type ProjectDetailTab = "info" | "diary" | "financial" | "documents";

interface ProjectHeaderWithTabsProps {
  projectId: string;
  project: { name: string; address?: string | null } | null;
  activeTab: ProjectDetailTab;
  canDelete: boolean;
  canManageMembers: boolean;
  hasFinancialAccess: boolean;
  onDeleteClick: () => void;
}

export default function ProjectHeaderWithTabs({
  projectId,
  project,
  activeTab,
  canDelete,
  canManageMembers,
  hasFinancialAccess,
  onDeleteClick,
}: ProjectHeaderWithTabsProps) {
  const tabRoutes: Record<ProjectDetailTab, string> = {
    info: `/projects/${projectId}`,
    diary: `/projects/${projectId}/diary`,
    financial: `/projects/${projectId}/financial`,
    documents: `/projects/${projectId}/uploads`,
  };

  const visibleTabs = useMemo(
    () =>
      (Object.keys(PROJECT_DETAIL_TABS) as ProjectDetailTab[]).filter(
        (tab) => tab !== "financial" || hasFinancialAccess
      ),
    [hasFinancialAccess]
  );

  return (
    <>
      <div className="flex flex-col gap-4 mb-8 md:flex-row md:justify-between md:items-start">
        <div className="min-w-0">
          <Link
            to="/dashboard"
            className="text-sm uppercase tracking-widest text-primary-600 dark:text-primary-400 hover:text-primary-950 dark:hover:text-white mb-4 cursor-pointer inline-block"
          >
            ← Voltar
          </Link>
          <h1 className="text-3xl font-light tracking-tight text-primary-950 dark:text-white mb-2 wrap-break-word">
            {project?.name}
          </h1>
          {project?.address ? (
            <p className="text-lg text-primary-600 dark:text-primary-400 wrap-break-word">
              {project.address}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-4 shrink-0">
          {canManageMembers && (
            <Button asChild variant="secondary">
              <Link to={`/projects/${projectId}/invite`}>
                Convidar Usuário
              </Link>
            </Button>
          )}
          <EditIconButton
            as="link"
            to={`/projects/${projectId}/edit`}
            aria-label="Editar Projeto"
          />
          {canDelete && (
            <DeleteIconButton
              onClick={onDeleteClick}
              aria-label="Excluir Projeto"
            />
          )}
        </div>
      </div>

      <Tabs value={activeTab}>
        <TabsList className="mb-8 overflow-x-auto">
          {visibleTabs.map((tab) => (
            <TabsTrigger key={tab} value={tab} asChild>
              <Link to={tabRoutes[tab]}>
                {PROJECT_DETAIL_TABS[tab]}
              </Link>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </>
  );
}
